import * as React from 'react';
import ReactDOM from 'react-dom/client';
import { Suspense, lazy } from 'react';
import './index.css';
import { setupBuildIdRefresh } from './utils/buildIdRefresh';
import { applyCssVariables } from './theme/applyCssVariables';
import { isMobileAuthRedirectPath } from './utils/mobileAuthRedirect';
import {
  captureGoogleChatOAuthCallback,
  runAfterGoogleChatCallback,
} from './pages/GoogleChatSettingsPage/oauthCallbackState';

captureGoogleChatOAuthCallback();

// Inject design-token CSS variables before first paint so `--color-*` is available
// to Tailwind utilities, the MUI theme, and plain CSS files (no FOUC).
applyCssVariables();

runAfterGoogleChatCallback(() => setupBuildIdRefresh({ buildId: __STREAMLINER_BUILD_ID__ }));

const normalizePathname = (pathname: string) => {
  const normalized = pathname.replace(/\/+$/, '');
  return normalized.length > 0 ? normalized : '/';
};

const isPublicBootstrapPath = (pathname: string) => {
  const normalizedPath = normalizePathname(pathname);
  const isAccountInvitationSetup = normalizedPath === '/account-invitation/setup';
  return (
    normalizedPath === '/onboarding' ||
    normalizedPath.startsWith('/login') ||
    isMobileAuthRedirectPath(normalizedPath) ||
    isAccountInvitationSetup ||
    normalizedPath === '/password/forgot/reset'
  );
};

if (typeof window !== 'undefined' && !isPublicBootstrapPath(window.location.pathname)) {
  const installTelemetry = () => {
    import('./utils/globalErrorTelemetry')
      .then(({ installGlobalErrorTelemetry }) => {
        installGlobalErrorTelemetry();
      })
      .catch(() => {
        // Ignore telemetry bootstrap failures to keep app boot resilient.
      });
  };

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => installTelemetry());
  } else {
    globalThis.setTimeout(installTelemetry, 0);
  }
}

const RootBootstrap = lazy(() =>
  isPublicBootstrapPath(window.location.pathname)
    ? import('./bootstrap/PublicBootstrap')
    : import('./bootstrap/PrivateBootstrap')
);

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found');
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <Suspense fallback={null}>
      <RootBootstrap />
    </Suspense>
  </React.StrictMode>
);
